/**
 * player_motion — the player's ground velocity from position samples, shared
 * by the settlement streamer and the far tier (fast-approach wave). One
 * sampler, called once per settlement tick (RenderSub 36) and read by the
 * proxies (RenderSub 39) in the same frame. Exponentially smoothed over ~0.5 s
 * so a network snap does not read as 500 m/s; a jump larger than a vehicle
 * could cover (a teleport / world entry) resets the filter instead.
 */
import { atlas } from '../state.js';

const TAU_S = 0.5;
/** Implied speed above this (m/s) is a teleport / world entry, not motion: 2× the ladder's saturation speed. */
const TELEPORT_SPEED = 640;
/** A gap longer than this (s) has no usable motion in it (a hidden tab, a stalled frame). */
const MAX_GAP_S = 120;

let lastX = NaN, lastZ = NaN, lastT = 0;
let vx = 0, vz = 0;

/** Feed the current player position; call once per tick with the timestamp (ms). */
export function samplePlayerMotion(nowMs: number, x = atlas.localX, z = atlas.localZ): void {
  if (!Number.isFinite(lastX)) { lastX = x; lastZ = z; lastT = nowMs; return; }
  const dt = (nowMs - lastT) / 1000;
  if (dt <= 0) return;
  const dx = x - lastX, dz = z - lastZ;
  lastX = x; lastZ = z; lastT = nowMs;
  // Speed-based, not distance-based: a headless probe (or a 5 fps client)
  // ticks seconds apart and covers hundreds of metres between samples at
  // cruise — that is motion, not a teleport.
  if (Math.hypot(dx, dz) / dt > TELEPORT_SPEED || dt > MAX_GAP_S) { vx = 0; vz = 0; return; }
  const a = 1 - Math.exp(-dt / TAU_S);
  vx += (dx / dt - vx) * a;
  vz += (dz / dt - vz) * a;
}

/** Smoothed ground velocity (m/s). Zero during space flight (the ladder is a ground concern). */
export function playerVelocity(): { vx: number; vz: number; speed: number } {
  if (atlas.spaceFlight) return { vx: 0, vz: 0, speed: 0 };
  return { vx, vz, speed: Math.hypot(vx, vz) };
}

/** Test hook: reset the filter. */
export function resetPlayerMotion(): void { lastX = NaN; lastZ = NaN; lastT = 0; vx = 0; vz = 0; }
